import { PlayerRef, Player as RemotionPlayer, RenderPoster } from '@remotion/player';
import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore, useState } from 'react';
import { AbsoluteFill } from 'remotion';
import { isEmpty } from 'radash';
import { SegmentType } from '@goldcast/player-config/src/types/Segment/Type';
import { TrackType } from '@goldcast/player-config/src/types/Track/Type';
import {
  SelectionElementType,
  CompositionConfig,
  ElementPositions
} from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/CompositionConfig';
import { Sequence } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Sequence';
import { SegmentsTrack } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Track/SegmentsTrack';
import { COMPOSITION_DIMENSIONS, VIDEO_QUALITY } from '@goldcast/player-config/src/constants/Remotion.constants';
import { roundToNDecimalPlaces } from '@goldcast/player-config/src/utils/mathUtils';
import {
  isClipAudiogram,
  isFullLengthClip,
  shouldShowLowResolutionPreview
} from '@goldcast/player-config/src/utils/clipUtils';
import ClipPlayerControlsV2 from './ClipPlayerControlsV2/ClipPlayerControlsV2';
import MainPlayer from './MainPlayer';
import { ResponsiveContainer } from '../../../components/atoms/ResponsiveContainer';
import { getClipPlayerElementId } from '../../../Pages/Clip/ClipPlayer/ClipPlayerUtils';
import { getAbsolutePlayerTime, getRelativePlayerTime } from './utils/deletesUtils';
import LowResolutionPreviewTag from './LowResolutionPreviewTag/LowResolutionPreviewTag';
import { RemotionPlayerContext } from './ClipPlayerV2WithNewTimeline/context/RemotionPlayerContext';
import { playerPlaybackRateStore } from './stores/playerPlaybackRateStore';
import { RemotionUsage, RemotionUsageContextProvider } from './RemotionUsageContext/RemotionUsageContext';
import { useClipPlayerThumbnail } from './useClipPlayerPlaceholder';
import ClipPlayerThumbnail from './ClipPlayerThumbnail';
import { useRemotionTimeSync } from './ClipPlayerV2WithNewTimeline/hooks/useRemotionTimeSync';
import PlayerMenus from '@/App/remotion/components/PlayerMenus/PlayerMenus';
import Loader from '@/components/atoms/Loader';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { classnames } from '@/libs/utils';
import ClipHeader from '@/components/molecules/ClipHeader';
import ClipCustomizerMenu from '@/Pages/Clip/ClipPlayer/ClipCustomizer/ClipCustomizerMenu';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import FullTranscript from '@/components/molecules/Transcript/FullTranscript';
import useTranscriptPage from '@/Pages/TranscriptPage/useTranscriptPage';
import { updatePlayerStoreWithId } from '@/stores/playerV2';
import { useTranscriptContext } from '@/context/TranscriptContext/TranscriptContext';
import { ClipPlayerSection } from '@/Pages/Clip/ClipPlayer/ClipPlayerSections/ClipPlayerSectionsTypes';
import { changeCaptionsPosition, isPlayRangeMicroCutsEnabled } from '@/stores/clip';
import {
  TIMELINE_DISPLAY_TYPE,
  TRANSCRIPT_DISPLAY_TYPE
} from '@/Pages/Clip/ClipPlayer/ClipTimelineV2/ClipTimelineTypes';
import { setCurrentTimeWithoutSeeking } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/store/remotionPlayerState';
import ClipRecommendationPlayerControls from '@/Pages/HomePageNew/ClipRecommendation/ClipRecommendationPlayerControls';
import { useNewVideoTagInPreview } from '@/hooks/useNewVideoTagInPreview';

export default function ClipPlayerV2({
  playerClasses = '',
  inlineEditEnabled = false,
  transcriptDisplayType,
  showClipHeader = false,
  timelineDisplayType = TIMELINE_DISPLAY_TYPE.PROGRESS_BAR,
  externalControls,
  isFullRecordingEdit = false,
  canEditCaptions = true,
  canEditSpeakerLabel = false,
  isSharedPage = false,
  canEditImages = false,
  canEditTextElements = false,
  canEditVideos = false,
  showThumbnailOnMount = false,
  showLockIconInHeader = false
}: {
  playerClasses?: string;
  inlineEditEnabled?: boolean;
  transcriptDisplayType?: TRANSCRIPT_DISPLAY_TYPE;
  showClipHeader?: boolean;
  timelineDisplayType?: TIMELINE_DISPLAY_TYPE;
  externalControls?: {
    autoPlay?: boolean;
    onPlay?: () => void;
    onPause?: () => void;
  };
  isFullRecordingEdit?: boolean;
  canEditCaptions?: boolean;
  canEditSpeakerLabel?: boolean;
  isSharedPage?: boolean;
  canEditImages?: boolean;
  canEditTextElements?: boolean;
  canEditVideos?: boolean;
  showThumbnailOnMount?: boolean;
  showLockIconInHeader?: boolean;
}) {
  const remotionPlayerRef = useRef<PlayerRef>(null);
  const { clipId, clipData, playerStore, fps } = useClipsContext();
  const transcriptStore = useTranscriptContext();
  const { createClip } = useTranscriptPage();
  const playerConfig = clipData.asset_metadata.player_config;

  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const playbackRate = useSyncExternalStore(playerPlaybackRateStore.subscribe, playerPlaybackRateStore.getSnapshot);
  const { showThumbnailImage, removeThumbnailImage } = useClipPlayerThumbnail({ showThumbnailOnMount });

  const [timeSections, setTimeSections] = useState<
    {
      fromFrame: number;
      toFrame: number;
      section: ClipPlayerSection;
      bounds?: { fromFrame: number; toFrame: number };
    }[]
  >([]);

  const isClipCustomizerMenuVisible = useMemo(() => {
    return inlineEditEnabled && !clipData.locked;
  }, [clipData.locked, inlineEditEnabled]);

  const clipDeleteBounds: Sequence[] = useMemo(() => {
    if (clipData.asset_metadata.deletes && !isEmpty(clipData.asset_metadata.deletes)) {
      return Object.values(clipData.asset_metadata.deletes)
        .map(d => ({
          fromFrame: d.bounds[0],
          toFrame: d.bounds[1]
        }))
        .sort((a, b) => a.fromFrame - b.fromFrame)
        .filter(d => d.fromFrame !== d.toFrame);
    }

    return [];
  }, [clipData.asset_metadata.deletes]);

  useEffect(() => {
    const segmentsTrack = playerConfig?.playerConfig.tracks.find(t => t.type === TrackType.SEGMENTS) as SegmentsTrack;
    const segments = segmentsTrack?.segments;

    if (!segments) {
      return;
    }

    const newTimeSections = segments.map((s, index) => ({
      fromFrame: s.fromFrame,
      toFrame: s.toFrame,
      section: (index === 0 && s.type === SegmentType.VIDEO
        ? 'intro'
        : index === segments.length - 1 && s.type === SegmentType.VIDEO
          ? 'outro'
          : 'main') as ClipPlayerSection,
      bounds: s.type === SegmentType.COMPOSITE ? s.maxBounds : undefined
    }));

    setTimeSections(newTimeSections);
  }, [playerConfig?.durationInFrames]);

  const playPlayer = useCallback(() => {
    remotionPlayerRef.current?.play();
    updatePlayerStoreWithId(clipId, { paused: false });
  }, [clipId]);

  const pausePlayer = useCallback(() => {
    remotionPlayerRef.current?.pause();
    updatePlayerStoreWithId(clipId, { paused: true });
  }, [clipId]);

  const isPaused = useMemo(() => playerStore.paused, [playerStore.paused]);

  const setCurrentSectionAndPlay = useCallback(
    (section: ClipPlayerSection, shouldPlay: boolean, time?: number) => {
      // Find the target section
      const targetSection = timeSections.find(s => s.section === section);
      if (!targetSection) {
        return;
      }

      // Calculate the absolute time within the video
      const absoluteTime = time !== undefined ? targetSection.fromFrame / fps + time : targetSection.fromFrame / fps;

      const timeoutWithDeletes = getAbsolutePlayerTime({
        // This is to offset for the clipData.asset_metadata.start in the ClipSpeakersTimelineView
        time: absoluteTime + (section === 'main' ? clipData.asset_metadata.start : 0),
        deleteBounds: clipDeleteBounds
      }); // Seek to the correct frame

      remotionPlayerRef.current?.seekTo(Math.round(timeoutWithDeletes * fps));
      setCurrentTimeWithoutSeeking(timeoutWithDeletes * fps);

      if (shouldPlay) {
        playPlayer();
      } else {
        pausePlayer();
      }
    },
    [timeSections, playPlayer, pausePlayer, clipDeleteBounds, clipData.asset_metadata.start, fps]
  );

  const startTimesAtSrtIndex = useMemo(() => {
    if (transcriptStore.timeSrtIndexArray.length === 0) {
      return [];
    }
    return transcriptStore.timeSrtIndexArray.map(a => a.startTime);
  }, [transcriptStore.timeSrtIndexArray]);

  const showPlayerFrames = featureFlags[FeatureFlagKeys.Use_CL_Show_Player_Frames];

  const { compositionHeight, compositionWidth } = useMemo(() => {
    const videoQuality = VIDEO_QUALITY.FULL_HD;
    const compositionHeight = COMPOSITION_DIMENSIONS[clipData.asset_metadata.size][videoQuality].HEIGHT;
    const compositionWidth = COMPOSITION_DIMENSIONS[clipData.asset_metadata.size][videoQuality].WIDTH;

    return {
      compositionHeight,
      compositionWidth
    };
  }, [clipData.asset_metadata.size]);

  const onPlayerTimeUpdate = useCallback(
    e => {
      const currentTime = roundToNDecimalPlaces(e.detail.frame / fps, 3);
      const newCurrentSection = timeSections.find(s => currentTime >= s.fromFrame && currentTime <= s.toFrame)?.section;
      if (newCurrentSection === 'main') {
        const mainSection = timeSections.find(s => s.section === 'main');
        const currentTimeForMainSection = currentTime - (mainSection?.fromFrame || 0) / fps;

        const currentTimeWithDeletes = getRelativePlayerTime({
          currentTime: currentTimeForMainSection,
          deleteBounds: clipDeleteBounds
        });

        updatePlayerStoreWithId(clipId, {
          isSegmentActive: true,
          currentTime: currentTimeWithDeletes + (mainSection?.fromFrame || 0) / fps
        });
      } else {
        updatePlayerStoreWithId(clipId, {
          isSegmentActive: false
        });
      }
    },
    [clipId, clipDeleteBounds, timeSections, fps]
  );

  useEffect(() => {
    const playerRef = remotionPlayerRef.current;
    if (playerRef) {
      playerRef.addEventListener('timeupdate', onPlayerTimeUpdate);
      playerRef.addEventListener('play', playPlayer);
      playerRef.addEventListener('pause', pausePlayer);
    }

    return () => {
      if (playerRef) {
        playerRef.removeEventListener('timeupdate', onPlayerTimeUpdate);
        playerRef.removeEventListener('play', playPlayer);
        playerRef.removeEventListener('pause', pausePlayer);
      }
    };
  }, [timeSections, onPlayerTimeUpdate, playPlayer, pausePlayer]);

  const introPlayerProps = useMemo(() => {
    const introSection = timeSections.find(s => s.section === 'intro');
    const currentTime = playerStore.currentTime;
    const isInIntroSection =
      introSection && currentTime * fps >= introSection.fromFrame && currentTime * fps <= introSection.toFrame;

    return {
      introCurrentTime: isInIntroSection ? currentTime - introSection.fromFrame / fps : 0,
      introDuration: introSection ? (introSection.toFrame - introSection.fromFrame) / fps : 0,
      isIntroPaused: isPaused,
      playIntroSection: playPlayer,
      pauseIntroSection: pausePlayer,
      // not needed for sure
      introRef: { current: null } as any,
      onIntroTimeUpdate: () => {},
      onIntroEnd: () => {},
      onLoadedIntroData: () => {}
    };
  }, [timeSections, playerStore.currentTime, isPaused, playPlayer, pausePlayer, fps]);

  useRemotionTimeSync({ playerRef: remotionPlayerRef.current, useTimeUpdate: true });

  /**
   * Update the player srt index based on time and the srt index of the words
   */
  useEffect(() => {
    if (startTimesAtSrtIndex.length === 0) {
      return;
    }

    const mainSectionCurrentTime = playerStore.currentTime - introPlayerProps.introDuration;
    const index = startTimesAtSrtIndex.findLastIndex(time => time <= mainSectionCurrentTime);

    updatePlayerStoreWithId(clipId, { currentSrtIndex: transcriptStore.timeSrtIndexArray[index]?.srtIndex });
  }, [
    clipId,
    playerStore.currentTime,
    startTimesAtSrtIndex,
    transcriptStore.timeSrtIndexArray,
    introPlayerProps.introDuration
  ]);

  useEffect(() => {
    return () => {
      setCurrentTimeWithoutSeeking(0);
    };
  }, []);

  const renderPoster: RenderPoster = useCallback(({ height, width, isBuffering }) => {
    if (isBuffering) {
      return (
        <AbsoluteFill
          className="absolute inset-0 overflow-hidden bg-slate-100 opacity-50"
          data-testid="composite-player-loader"
          id="composite-player-loader"
        >
          <div className="absolute inset-0 flex animate-pulse items-center justify-center overflow-hidden bg-gray-200 opacity-50">
            <Loader />
          </div>
        </AbsoluteFill>
      );
    }

    return (
      <AbsoluteFill style={{ backgroundColor: 'gray' }}>
        Click to play! ({height}x{width})
      </AbsoluteFill>
    );
  }, []);

  const onCreateClip = useCallback(
    (startTime: number | null, endTime: number | null, _startSegmentId?: string, _endSegmentId?: string) => {
      if (startTime === null || endTime === null) {
        return Promise.reject(new Error('Start time or end time is null'));
      }

      return createClip(startTime, endTime, {
        edits: clipData.asset_metadata.edits,
        deletes: clipData.asset_metadata.deletes || {}
      });
    },
    [clipData.asset_metadata.deletes, clipData.asset_metadata.edits, createClip]
  );

  const playMainSectionFromTime = useCallback(
    (time: number, shouldPlay?: boolean) => {
      setCurrentSectionAndPlay('main', shouldPlay || !isPaused, time);
    },
    [setCurrentSectionAndPlay, isPaused]
  );

  const onOutlineRelease = useCallback(
    (positions: ElementPositions) => {
      changeCaptionsPosition({
        clipId,
        caption_positions: {
          x: positions.left as any,
          y: positions.top as any,
          width: positions.width as any,
          height: positions.height as any
        },
        skipUpdate: false
      });
    },
    [clipId]
  );

  const onOutlineResize = useCallback(
    (positions: ElementPositions, selectionElementType: SelectionElementType) => {
      if (selectionElementType === SelectionElementType.CAPTIONS) {
        changeCaptionsPosition({
          clipId,
          caption_positions: {
            x: positions.left as any,
            y: positions.top as any,
            width: positions.width as any,
            height: positions.height as any
          },
          skipUpdate: true
        });
      }
    },
    [clipId]
  );
  const isFullClip = useMemo(() => {
    return isFullLengthClip(clipData);
  }, [clipData]);

  const inputProps = useMemo((): CompositionConfig => {
    if (playerConfig && !isEmpty(playerConfig)) {
      return {
        ...playerConfig,
        showPlayerFrames,
        onOutlineRelease,
        onOutlineResize,
        canEditCaptions,
        disableCaptionsDragging: isClipAudiogram(clipData),
        disableCaptionsResize: isClipAudiogram(clipData),
        canEditSpeakerLabel,
        canEditImages,
        canEditTextElements,
        canEditVideos
      };
    }
    return {} as CompositionConfig;
  }, [
    playerConfig,
    showPlayerFrames,
    onOutlineRelease,
    onOutlineResize,
    canEditCaptions,
    canEditSpeakerLabel,
    canEditImages,
    canEditTextElements,
    canEditVideos,
    clipData
  ]);

  const isExternalControls = useMemo(() => {
    return timelineDisplayType === TIMELINE_DISPLAY_TYPE.EXTERNAL;
  }, [timelineDisplayType]);

  const { useNewVideoTag } = useNewVideoTagInPreview();

  return (
    <div
      id={getClipPlayerElementId(clipId)}
      className={classnames('group flex size-full grow flex-col items-center justify-center overflow-hidden', {
        'px-3': isFullRecordingEdit
      })}
    >
      <div className="flex size-full flex-row items-center justify-between">
        {transcriptDisplayType === TRANSCRIPT_DISPLAY_TYPE.FULL ? (
          <div className="relative h-full w-[calc(34rem-10px)] py-4">
            <FullTranscript
              videoAssetId={clipId}
              title={isFullClip ? 'Recording' : 'Clip'}
              onCreateClip={onCreateClip}
              onPause={pausePlayer}
              fullClipOffset={introPlayerProps.introDuration}
            />
          </div>
        ) : (
          <></>
        )}
        <div
          className={classnames(
            'flex h-full flex-col',
            transcriptDisplayType === TRANSCRIPT_DISPLAY_TYPE.FULL ? 'w-[calc(100%-34rem)]' : 'w-full'
          )}
        >
          {showClipHeader && <ClipHeader showLock={showLockIconInHeader} />}
          <div className={classnames('relative flex flex-1 items-center gap-4 rounded-md p-4 pr-0', playerClasses)}>
            <ResponsiveContainer
              id="player-main-section"
              compositionHeight={compositionHeight}
              compositionWidth={compositionWidth}
              parentHeight={isExternalControls ? '100%' : '80%'}
              parentWidth={isExternalControls ? '100%' : '80%'}
              childClassnames="m-auto"
              parentClassnames={classnames('flex-1', { 'p-4': !isExternalControls })}
            >
              {!isExternalControls && (
                <div className="absolute inset-x-0 top-[-54px] z-[60]">
                  <PlayerMenus />
                </div>
              )}
              {/* ! TODO: @AshwinBhatkal to handle the state updates better */}
              {playerConfig && !isEmpty(playerConfig) && (
                <RemotionUsageContextProvider
                  useNewVideoTag={useNewVideoTag}
                  usage={RemotionUsage.PLAYER}
                  isSharedPage={isSharedPage}
                  isPlayRangeMicroCutsEnabled={isPlayRangeMicroCutsEnabled()}
                >
                  <RemotionPlayer
                    component={showThumbnailImage ? ClipPlayerThumbnail : MainPlayer}
                    durationInFrames={Math.floor(playerConfig.durationInFrames || 0)}
                    compositionWidth={compositionWidth}
                    compositionHeight={compositionHeight}
                    fps={fps}
                    ref={remotionPlayerRef}
                    clickToPlay={false}
                    spaceKeyToPlayOrPause={false}
                    controls={showPlayerFrames}
                    inputProps={inputProps}
                    style={{
                      width: '100%'
                    }}
                    showVolumeControls={!isExternalControls}
                    renderPoster={renderPoster}
                    showPosterWhenBuffering
                    playbackRate={playbackRate}
                  />
                </RemotionUsageContextProvider>
              )}
              <LowResolutionPreviewTag isVisible={shouldShowLowResolutionPreview(clipData) && !showThumbnailImage} />
            </ResponsiveContainer>
            {isClipCustomizerMenuVisible && (
              <ClipCustomizerMenu
                isFullRecordingEdit={isFullRecordingEdit}
                onTimeUpdate={playMainSectionFromTime}
                useNewRecordingsPlayer
              />
            )}
          </div>
        </div>
      </div>
      {timelineDisplayType === TIMELINE_DISPLAY_TYPE.EXTERNAL ? (
        <RemotionPlayerContext.Provider value={{ playerRef: remotionPlayerRef.current }}>
          <ClipRecommendationPlayerControls {...externalControls} />
        </RemotionPlayerContext.Provider>
      ) : timelineDisplayType === TIMELINE_DISPLAY_TYPE.PROGRESS_BAR ? (
        <RemotionPlayerContext.Provider value={{ playerRef: remotionPlayerRef.current }}>
          <ClipPlayerControlsV2 removeThumbnailImage={removeThumbnailImage} />
        </RemotionPlayerContext.Provider>
      ) : (
        <></>
      )}
    </div>
  );
}
